/* eslint-disable @typescript-eslint/no-empty-object-type */
import { fetchJson, fetchText, http, HttpOptions } from './http';
import { format, parse } from 'url';
import { remoteDataError } from 'app/infrastructure/remote-data/http';
import { concatUrl } from 'app/infrastructure/universal/url';
import { AppSetting } from 'dependencies/appsettings';
import { authProvider } from 'dependencies/auth';
import { HttpStatusCodes } from './constants';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { Store } from 'redux';
import { AgentSubscriber, HttpAgent, Message } from "@ag-ui/client";

export type Id = number | string;

let store: Store<ApplicationState> = undefined;
export function connectToStore(newStore: Store<ApplicationState>) {
  store = newStore;
}

type RunAgentArgs = {
  tenant: string;
  context: string;
  threadId: string;
  messages: Message[];
  subscriber: AgentSubscriber;
  abortController?: AbortController;
};
export interface Resource<T> {
  all(): Promise<T>;
  single(): Promise<T>;
  text(): Promise<string>;
  response(): Promise<T>;
  create(obj: Object, getLocation?: boolean): Promise<T>; // post && returns new id or header.location resource
  save(obj?: Object): Promise<T>;
  update(obj: Object): Promise<T>;
  httpDelete(obj: Object): Promise<T>;
  blob(obj?: Object): Promise<BlobResponse>;
  withId(id: Id): Resource<T>;
  withIfMatch(etag: string): Resource<T>;
  withIfNoneMatch(etag: string): Resource<T>;
  withHeader(name: string, value: string): Resource<T>;
  withImportance(importance: 'low' | 'high'): Resource<T>;
  filter(keyValues: Object): Resource<T>;
}

export interface RemoteDataFn<T> {
  (): Resource<T>;
}

function makeHttpRequest(url: string, options: HttpOptions = {}): Promise<Response> {
  return http(url, authProvider.processResponse, options);
}

/**
 * Resolve a clean URL from baseUrl, path and querystring
 */
export function resolveUrl(baseUrl: string, query?: Object, ...paths: any[]) {

  const urlObject: any = parse(baseUrl || '/');

  urlObject.pathname = concatUrl(urlObject.pathname, ...paths);
  urlObject.query = query;

  return format(urlObject);
}

/**
 * Return one remote entity using fetch() and json()
 *
 * @template T
 * @param {string} url
 * @param {Id} [id]
 * @param {string} [query]
 * @returns {Promise<T>}
 */
async function single<T>(
  baseURL: string,
  endpoint: string,
  headers: ResourceHeader,
  id?: Id,
  query?: Object,
  importance?: string): Promise<T> {

  const targetUrl = resolveUrl(baseURL, query, endpoint, id);
  const result = await fetchJson(targetUrl, authProvider.processResponse, { headers, importance });
  if (Array.isArray(result)) {
    return result[0];
  }
  return result;
}

/**
 * Return one remote entity using fetch() and text()
 *
 * @param {string} url
 * @param {Id} [id]
 * @param {string} [query]
 * @returns {Promise<string>}
 */
async function text(
  baseURL: string,
  endpoint: string,
  headers: ResourceHeader,
  id?: Id,
  query?: Object,
  importance?: string): Promise<string> {

  const targetUrl = resolveUrl(baseURL, query, endpoint, id);
  return await fetchText(targetUrl, authProvider.processResponse, { headers, importance });
}

/**
 * Return everything form the result.body using fetch().json()
 *
 * @template T
 * @param {string} url
 * @param {Id} [id]
 * @param {string} [query]
 * @returns {Promise<T>}
 */
async function all<T>(
  baseURL: string,
  endpoint: string,
  headers: ResourceHeader,
  id?: Id,
  query?: Object,
  importance?: string): Promise<T> {

  const targetUrl = resolveUrl(baseURL, query, endpoint, id);
  return fetchJson(targetUrl, authProvider.processResponse, { headers, importance });
}

/**
 * Return HttpResponse using fetch()
 *
 * @template T
 * @param {string} url
 * @param {Id} [id]
 * @param {string} [query]
 * @returns {Promise<T>}
 */
async function response<T>(
  baseURL: string,
  endpoint: string,
  headers: ResourceHeader,
  id?: Id,
  query?: Object,
  importance?: string): Promise<T> {

  const targetUrl = resolveUrl(baseURL, query, endpoint, id);

  const response = await makeHttpRequest(targetUrl, { headers, importance });

  const json = response.status !== HttpStatusCodes.NO_CONTENT && response.status !== HttpStatusCodes.NOT_MODIFIED
    ? await response.json() : undefined;

  return {
    json: json,
    status: response.status,
    ...(response.headers.has('ETag') ? { etag: response.headers.get('ETag') } : {}),
    ...(!!response.headers.has('Expires') && { expires: response.headers.get('Expires') })
  } as any;
}

/**
 * Perform a http POST and return the response of fetching header.location,
 * or if `; returnLocation` is false, returns the response
 *
 * @template T
 * @param {string} url the url to POST the request to
 * @param {Object} [params] body form values to post
 * @param {boolean} returnLocation return the resource specified by header.location when statusCode is 201 CREATED. default is true.
 * @param {Id} [id]
 * @param {string} [query]
 * @returns {Promise<T>}
 */
async function post(
  baseURL: string,
  endpoint: string,
  headers: ResourceHeader,
  params: Object,
  fetchLocationId: boolean = false,
  id?: Id,
  query?: Object,
  importance?: string): Promise<Response | { location: string; response: any }> {

  const result = await makeHttpRequest(resolveUrl(baseURL, query, endpoint, id), { method: 'post', body: params, headers, importance });

  if (!fetchLocationId) {
    return Promise.resolve(result);
  }

  if (result.ok && (result.status === HttpStatusCodes.OK || result.status === HttpStatusCodes.CREATED)) {
    const caseEntityUrl = result.headers.get('Location');
    if (!caseEntityUrl) {
      throw remoteDataError('Header "Location" is not exposed for CORS', result);
    }

    return Promise.resolve({
      location: caseEntityUrl.match(/(?:[^\\/](?!(\\|\/)))+$/)[0],
      response: await result.json()
    });
  }

  return Promise.resolve(result);
}

/**
 * Perform a http PUT and return the response
 *
 * @template T
 * @param {string} url the url to PUT the request to
 * @param {ResourceHeader} headers
 * @param {Object} params body form values to post
 * @param {Id} [id]
 * @param {string} [query]
 * @returns {Promise<T>}
 */
async function put<T>(
  baseURL: string,
  endpoint: string,
  headers: ResourceHeader,
  params?: Object,
  id?: Id,
  query?: Object,
  importance?: string): Promise<T> {

  const response = await makeHttpRequest(
    resolveUrl(baseURL, query, endpoint, id),
    { method: 'put', body: params || {}, headers: headers, importance }
  );

  // response 204 may be null
  const body = await response.text();

  return {
    json: (body.length ? JSON.parse(body) : undefined),
    ...(response.headers.has('ETag') ? { etag: response.headers.get('ETag') } : {})
  } as any;
}

/**
 * Perform a http PATCH and return the response
 *
 * @template T
 * @param {string} url the url to PUT the request to
 * @param {ResourceHeader} headers
 * @param {Object} params body form values to post
 * @param {Id} [id]
 * @param {string} [query]
 * @returns {Promise<T>}
 */
async function patch<T>(
  baseURL: string,
  endpoint: string,
  headers: ResourceHeader,
  params: Object,
  id?: Id,
  query?: Object,
  importance?: string): Promise<T> {

  const response = await makeHttpRequest(
    resolveUrl(baseURL, query, endpoint, id),
    { method: 'PATCH', body: params || {}, headers: headers, importance }
  );

  // response 204 may be null
  const body = await response.text();

  return {
    json: (body.length ? JSON.parse(body) : undefined),
    ...(response.headers.has('ETag') ? { etag: response.headers.get('ETag') } : {})
  } as any;
}

/**
 * Perform a http DELETE and return the response.
 * Delete is reserved word in JS, thus function name breaking the convention.
 *
 * @template T
 * @param {string} url the url to DELETE the request to
 * @param {ResourceHeader} headers
 * @param {Object} params body form values to post
 * @param {Id} [id]
 * @param {string} [query]
 * @returns {Promise<T>}
 */
async function httpDelete(
  baseURL: string,
  endpoint: string,
  headers: ResourceHeader,
  params: Object,
  id?: Id,
  query?: Object,
  importance?: string): Promise<Response> {

  return await makeHttpRequest(resolveUrl(baseURL, query, endpoint, id), { method: 'delete', body: params, headers, importance });
}

/**
 * Perform a http request and return blob response
 *
 * @template T
 * @param {string} url
 * @param {ResourceHeader} headers
 * @param {string} [query]
 * @param {Object} [params]
 * @returns {Promise<T>}
 */
async function blob(
  baseURL: string,
  endpoint: string,
  headers: ResourceHeader,
  query?: Object,
  params?: Object,
  importance?: string): Promise<any> {

  const response = await makeHttpRequest(resolveUrl(baseURL, query, endpoint),
    params
      ? { method: 'post', body: params, headers, importance }
      : { headers, importance }
  );

  const blob = await response.blob();
  return { blob: blob, headers: response.headers };
}
export interface BlobResponse {
  blob: Blob;
  headers: Headers;
}

/**
 * Typed builder class for a fluent construction of REST requests
 *
 * @class ResourceWrapper
 * @implements {Resource<T>}
 * @template T
 */
interface ResourceHeader {
  'If-Match': string;
}

class ResourceWrapper<T> implements Resource<T> {

  private baseUrl: string;
  private resourceUrl: string;
  private authHeaders: any;
  private id: Id;
  private query: Object = {};
  private headerItems: ResourceHeader = {
    'If-Match': ''
  };
  private requestAuthentication: () => void;
  private importance: string = 'high';

  private async headers() {
    const authHeaders = await this.authHeaders();
    
    return {
      ...authHeaders,
      ...this.headerItems
    };
  }

  constructor(baseUrl: string, resourceUrl: string, authHeaders: {}, requestAuthentication: () => void) {
    this.baseUrl = baseUrl;
    this.resourceUrl = resourceUrl;
    this.authHeaders = authHeaders;
    this.requestAuthentication = requestAuthentication;

    if (store) {
      const state = store.getState()[STATE.remoteData];
      if (state) {
        this.query = {
          ...this.query,
          ...state.query
        };
      }
    }
  }

  /**
   * Adds the parameter as a route parameter
   *
   * @example
   * ```
   * .withId(123);  =>  /your/url / 123
   * ```
   *
   * @param {Id} id
   * @returns
   *
   * @memberOf ResourceWrapper
   */
  public withId = (id: Id) => {
    this.id = id;
    return this as Resource<T>;
  };

  /**
   * Set your last known entity Etag for every GET, POST, PUT, PATCH avoid errors
   * and improve user agent caching
   */
  public withIfMatch(etag: string) {
    this.headerItems['If-Match'] = etag;
    return this as Resource<T>;
  }

  /**
   * Set your last known entity Etag for every GET avoid errors
   * and improve user agent caching
   */
  public withIfNoneMatch(etag: string) {
    this.headerItems['If-None-Match'] = etag;
    return this as Resource<T>;
  }

  public withHeader(name: string, value: string) {
    if (name && value) {
      this.headerItems[name] = value;
    }
    return this as Resource<T>;
  }

  public withImportance(importance: 'low' | 'high') {
    this.importance = importance;
    return this;
  }

  /**
   * Adds a query string to the URL
   *
   *
   * ```
   *   {
   *     someparam: 'ok'  // => results in:  /your/url?someparam=ok
   *   }
   * ```
   *
   * @param map
   * @returns
   *
   * @memberOf ResourceWrapper
   */
  public filter(map: any) {
    this.query = Object.keys(map)
      .filter(p => !!p)
      .reduce((res, prop: any) => {
        if (map[prop] !== undefined && map[prop] !== null) {
          res[prop] = map[prop];
        }
        return res;
      }, this.query || {});
    return this as Resource<T>;
  }

  private invoke = async (fn: any) => {
    try {
      return fn();
    } catch (err) {
      if (err && err.status === HttpStatusCodes.UNAUTHORIZED) {
        this.requestAuthentication();
      } else {
        throw err;
      }
    }
  };
  /**
   * Return one (or the first) single entity from the service result
   *
   * @returns {Promise<string>}
   *
   * @memberOf ResourceWrapper
   */
  public async single(): Promise<T> {
    const headers = await this.headers();
    return this.invoke(async () => single<T>(this.baseUrl, this.resourceUrl, headers, this.id, this.query, this.importance));
  }

  public async text(): Promise<string> {
    return this.invoke(async () => text(this.baseUrl, this.resourceUrl, await this.headers(), this.id, this.query, this.importance));
  }

  /**
   * Return all of response.body as json from the service result
   *
   * @returns {Promise<T>}
   *
   * @memberOf ResourceWrapper
   */
  public async all(): Promise<T> {
    return this.invoke(async () => all<T>(this.baseUrl, this.resourceUrl, await this.headers(), this.id, this.query, this.importance));
  }

  /**
   * Return response from the service result
   *
   * @returns {Promise<T>}
   *
   * @memberOf ResourceWrapper
   */
  public async response(): Promise<T> {
    return this.invoke(async () => response<T>(this.baseUrl, this.resourceUrl, await this.headers(), this.id, this.query, this.importance));
  }

  /**
   * Create a new resource with http POST
   */
  public async create(params: Object, getLocation: boolean = false): Promise<T> {
    return this.invoke(async () => post(this.baseUrl, this.resourceUrl, await this.headers(), params, getLocation, this.id, this.query, this.importance));
  }

  /**
   * Create a new resource with http PUT
   */
  public async save(params?: Object): Promise<T> {
    return this.invoke(async () => put<T>(this.baseUrl, this.resourceUrl, await this.headers(), params, this.id, this.query, this.importance));
  }

  /**
   * Create a new resource with http PATCH
   */
  public async update(params: Object): Promise<T> {
    return this.invoke(async () => patch<T>(this.baseUrl, this.resourceUrl, await this.headers(), params, this.id, this.query, this.importance));
  }

  /**
   * Deletes resource with http DELETE
   */
  public async httpDelete(params: Object): Promise<T> {
    return this.invoke(async () => httpDelete(this.baseUrl, this.resourceUrl, await this.headers(), params, this.id, this.query, this.importance));
  }

  /**
   * Return response with blob
   */
  public async blob(params?: Object): Promise<BlobResponse> {
    return this.invoke(async () => blob(this.baseUrl, this.resourceUrl, await this.headers(), this.query, params, this.importance));
  }
}

export interface RemoteDataOptions {
  baseUrl: string;
  endpoint: string;
  referenceToken?: string;
}

/**
 * Offers a typed fluent interface over a remote REST api
 *
 * @export
 * @class RemoteData
 */
export class RemoteData {
  public config: RemoteDataOptions;
  public auth: any;

  //TODO: decide if RemoteData should live in the application layer, or have url and token parameterized
  constructor(endpoint: string) {
    this.auth = authProvider;
    this.config = {
      baseUrl: AppSetting.ApiBaseUrl,
      endpoint: endpoint
    };
  }

  /**
   * Returns a wrapping object for the generic resource type to build a remote request
   *
   * @template T
   * @returns {Resource<T>}
   *
   * @memberOf RemoteData
   */
  public resource<T>(): Resource<T> {
    return new ResourceWrapper<T>(this.config.baseUrl, this.config.endpoint, this.auth.headers, this.auth.reAuthenticate);
  }
}

export class RemoteAdminData extends RemoteData {

  constructor(endpoint: string) {
    super(endpoint);
    this.config.baseUrl = AppSetting.AdminApiBaseUrl;
  }
}

export class RemoteNotificationsData extends RemoteData {

  constructor(endpoint: string) {
    super(endpoint);
    this.config.baseUrl = AppSetting.NotificationsApiBaseUrl;
  }
}

export class RemoteAgUiData extends RemoteData {

  constructor(endpoint: string) {
    super(endpoint);
    this.auth = authProvider;
    this.config = {
      baseUrl: AppSetting.AiChatAssistantBaseUrl,
      endpoint: endpoint
    };
  }

  private async headers(tenant: string) {
    const authHeaders = await this.auth.headers();
    return {
      ...authHeaders,
      "X-Tenant": tenant
    };
  }

  public async run({
    tenant,
    context,
    threadId,
    messages,
    subscriber,
    abortController
  }: RunAgentArgs): Promise<void> {
    if (!tenant) {
      return;
    }
    const headers = await this.headers(tenant);
    const url = concatUrl(this.config.baseUrl, this.config.endpoint);
    const agent = new HttpAgent({ 
      url, 
      headers,
      threadId
    });

    agent.setMessages(messages);
    await agent.runAgent(
      { 
        context: [{ description: "context", value: context }],
        abortController
      },
      subscriber
    );
  }
}
